import { useCallback, useState } from 'react';

import type { ApprovalsContextValue } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { expValEquals } from '@confluence/feature-experiments';

type PublishWithAnalytics = (
	source?: string,
	analyticsSource?: string,
	shouldNotifyOverride?: boolean,
) => Promise<void>;

type ApprovalPublishConfirmation = {
	publishWithApprovalConfirmation: PublishWithAnalytics;
	isReplaceApprovalModalOpen: boolean;
	otherModeReviewStatus: ApprovalsContextValue['otherModeReviewStatus'];
	onReplaceApprovalConfirm: () => Promise<void>;
	onReplaceApprovalClose: () => void;
};

export const useApprovalPublishConfirmation = (
	publishWithAnalytics: PublishWithAnalytics,
): ApprovalPublishConfirmation => {
	const [isReplaceApprovalModalOpen, setIsReplaceApprovalModalOpen] = useState<boolean>(false);
	const [pendingPublishArgs, setPendingPublishArgs] = useState<{
		source: string;
		analyticsSource?: string;
		shouldNotifyOverride?: boolean;
	} | null>(null);
	const { approvalRequest, approvalRequestLoading, disallowFirstPublish, otherModeReviewStatus } =
		useApprovals();

	const shouldShowReplaceApprovalModal = useCallback(() => {
		if (!expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
			return false;
		}

		const isDraftApproved = approvalRequest?.review?.reviewStatus === 'APPROVED';
		// Let the publish guard show the required-approval blocker before offering to cancel
		// a review on the published version. Loading approval data must also go through the guard.
		if (
			disallowFirstPublish &&
			(approvalRequestLoading || !isDraftApproved) &&
			expValEquals('confluence_approvals_disallow_all_publish', 'isEnabled', true)
		) {
			return false;
		}

		// An approved draft with no pending review on the published version needs no warning.
		const isViewModePending = otherModeReviewStatus === 'PENDING';
		if (isDraftApproved && !isViewModePending) {
			return false;
		}

		return otherModeReviewStatus === 'PENDING' || otherModeReviewStatus === 'APPROVED';
	}, [
		approvalRequest?.review?.reviewStatus,
		approvalRequestLoading,
		disallowFirstPublish,
		otherModeReviewStatus,
	]);

	const openReplaceApprovalModal = useCallback(
		(source: string, analyticsSource?: string, shouldNotifyOverride?: boolean) => {
			setPendingPublishArgs({ source, analyticsSource, shouldNotifyOverride });
			setIsReplaceApprovalModalOpen(true);
		},
		[],
	);

	const onReplaceApprovalConfirm = useCallback(async () => {
		setIsReplaceApprovalModalOpen(false);
		setPendingPublishArgs(null);
		if (pendingPublishArgs) {
			await publishWithAnalytics(
				pendingPublishArgs.source,
				pendingPublishArgs.analyticsSource,
				pendingPublishArgs.shouldNotifyOverride,
			);
		}
	}, [pendingPublishArgs, publishWithAnalytics]);

	const onReplaceApprovalClose = useCallback(() => {
		setIsReplaceApprovalModalOpen(false);
		setPendingPublishArgs(null);
	}, []);

	const publishWithApprovalConfirmation = useCallback(
		async (source = 'shortcut', analyticsSource?: string, shouldNotifyOverride?: boolean) => {
			if (shouldShowReplaceApprovalModal()) {
				openReplaceApprovalModal(source, analyticsSource, shouldNotifyOverride);
			} else {
				await publishWithAnalytics(source, analyticsSource, shouldNotifyOverride);
			}
		},
		[shouldShowReplaceApprovalModal, openReplaceApprovalModal, publishWithAnalytics],
	);

	return {
		publishWithApprovalConfirmation,
		isReplaceApprovalModalOpen,
		otherModeReviewStatus,
		onReplaceApprovalConfirm,
		onReplaceApprovalClose,
	};
};
